import { useRef, type PointerEvent as ReactPointerEvent } from 'react'

export interface SwipeOptions {
  onSwipeLeft?: (() => void) | undefined
  onSwipeRight?: (() => void) | undefined
  /** Minimum horizontal travel before it counts as a swipe. */
  threshold?: number
  /**
   * Gestures starting this close to a screen edge are ignored, so the platform
   * back-swipe keeps working instead of fighting us for the same drag.
   */
  edgeInset?: number
}

/**
 * Horizontal swipe detection for the day view.
 *
 * Constrained to the horizontal axis — a drag only counts when it is clearly more
 * sideways than vertical, so it never hijacks list scrolling. Pair with
 * `touch-action: pan-y` on the element so vertical scrolling stays native.
 */
export function useHorizontalSwipe({
  onSwipeLeft,
  onSwipeRight,
  threshold = 60,
  edgeInset = 28,
}: SwipeOptions) {
  const start = useRef<{ x: number; y: number; valid: boolean } | null>(null)

  function onPointerDown(event: ReactPointerEvent) {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    const fromEdge =
      event.clientX <= edgeInset || event.clientX >= window.innerWidth - edgeInset
    start.current = { x: event.clientX, y: event.clientY, valid: !fromEdge }
  }

  function finish(event: ReactPointerEvent) {
    const from = start.current
    start.current = null
    if (!from || !from.valid) return

    const dx = event.clientX - from.x
    const dy = event.clientY - from.y

    if (Math.abs(dx) < threshold) return
    // Clearly horizontal, not a diagonal scroll.
    if (Math.abs(dx) <= Math.abs(dy) * 1.5) return

    if (dx < 0) onSwipeLeft?.()
    else onSwipeRight?.()
  }

  return {
    onPointerDown,
    onPointerUp: finish,
    onPointerCancel: () => {
      start.current = null
    },
  }
}
